/* eslint-disable max-len */
import { ClipboardIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import {
  Clock,
  Loader2,
  Pause,
  Settings2,
  Zap,
} from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import SlackIcon from "../../../../../assets/images/slack.svg?react";
import { Badge } from "../../../../../components/ui/badge";
import { cn } from "../../../../../lib/utils";
import { useAllAutomations } from "../../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";

type SignalsTableProps = {
  selectedFilter: string;
  selectedState: string;
  automationType?: string;
};

type DestinationMeta = {
  key: string;
  label: string;
  Icon: React.ComponentType<any>;
};

const destinationMap: Record<string, DestinationMeta> = {
  send_to_slack: { key: "send_to_slack", label: "Slack", Icon: SlackIcon },
  send_to_inbox: { key: "send_to_inbox", label: "Inbox", Icon: ClipboardIcon },
  send_to_email: { key: "send_to_email", label: "Email", Icon: EnvelopeIcon },
};

const fallbackIcon = Settings2;

const getRelativeDate = (value?: string) => {
  if (!value) return "just now";

  try {
    return `${formatDistanceToNowStrict(parseISO(value), { addSuffix: true })}`;
  } catch {
    return value;
  }
};

const SignalsTable = ({
  selectedFilter,
  selectedState,
  automationType = "signal",
}: SignalsTableProps) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const navigate = useNavigate();

  const allAutomations: any = useAllAutomations({
    workspaceId: workspace?.id,
    automationType,
    enabled: !!workspace?.id,
  });

  const automations = useMemo(() => {
    const source = Array.isArray(allAutomations?.data) ? allAutomations.data : [];

    return source
      .filter((item: any) => {
        if (selectedFilter === "all") return true;

        return item?.automation_actions?.actions?.some((action: any) => action?.type === selectedFilter);
      })
      .filter((item: any) => {
        if (selectedState === "all") return true;

        return String(item?.status || "").toLowerCase() === selectedState;
      })
      .sort((left: any, right: any) => {
        const leftDate = Date.parse(left?.updated_at || left?.created_at || "") || 0;
        const rightDate = Date.parse(right?.updated_at || right?.created_at || "") || 0;
        return rightDate - leftDate;
      });
  }, [allAutomations?.data, selectedFilter, selectedState]);

  if (allAutomations?.isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (allAutomations?.isError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Unable to load automations. Please try again.
        </div>
      </div>
    );
  }

  if (!automations.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-16">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Zap className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-foreground">No {automationType === "signal" ? "signals" : "automations"} found</p>
        <p className="text-xs text-muted-foreground">Try changing filters or create a new one.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto max-w-4xl space-y-4">
        {automations.map((automation: any) => {
          const trigger = String(automation?.trigger || "").toLowerCase();
          const CardIcon = trigger.includes("usage") ? Zap : fallbackIcon;
          const actionTypes = (automation?.automation_actions?.actions || []).map((action: any) => action?.type);
          const destinations = Object.keys(destinationMap)
            .filter((key) => actionTypes.includes(key))
            .map((key) => destinationMap[key]);

          const isPaused = String(automation?.status || "").toLowerCase() === "paused";
          const statusLabel = isPaused ? "paused" : "active";
          const ownerLabel = automation?.owner_id || "Workspace";
          const createdAtLabel = getRelativeDate(automation?.created_at || automation?.updated_at);

          return (
            <div
              key={automation.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (automationType === "signal" && workspace?.unique_identifier) {
                  navigate(`/home/${workspace.unique_identifier}/signals/ai-builder?signalId=${automation.id}`);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && automationType === "signal" && workspace?.unique_identifier) {
                  navigate(`/home/${workspace.unique_identifier}/signals/ai-builder?signalId=${automation.id}`);
                }
              }}
              className={cn(
                "rounded-lg border border-border p-5 transition-all",
                automationType === "signal"
                  ? "cursor-pointer hover:border-foreground/20"
                  : "cursor-default",
              )}
            >
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-muted p-2.5">
                  <CardIcon className="h-5 w-5 text-muted-foreground" />
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="mb-1 truncate text-sm font-medium text-foreground">{automation?.name || "Untitled"}</h3>
                  <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">{automation?.description || "No description"}</p>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">{ownerLabel}</span>
                      <div className="flex items-center gap-1.5">
                        {isPaused ? (
                          <Pause className="h-3 w-3 text-muted-foreground" />
                        ) : (
                          <span className="h-2 w-2 rounded-full bg-status-active" />
                        )}
                        <span className="text-xs capitalize text-muted-foreground">{statusLabel}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        {destinations.map((destination) => {
                          const { Icon } = destination;
                          return (
                            <span
                              key={`${automation.id}-${destination.key}`}
                              className="inline-flex h-5 w-5 items-center justify-center rounded bg-muted"
                              title={destination.label}
                            >
                              <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                            </span>
                          );
                        })}
                      </div>

                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{createdAtLabel}</span>
                      </div>
                    </div>
                  </div>

                  {automation?.entity_relation && (
                    <div className="mt-3">
                      <Badge variant="outline" className="border-border-chip bg-muted text-xs font-normal text-muted-foreground">
                        {String(automation.entity_relation)}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SignalsTable;
